/**
 * Системийн тохиргоо — `/setup/system`.
 *
 * Гурван ТАБ (админ): Backup · Хэрэглэгчийн эрх · Сүүлийн үйлдлүүд.
 *
 * ⚠️ ХАСАГДСАН (2026-10-05, захиалагчийн цэгцлэл №18, №20):
 *    · «Хуваарьт ажил» — дөрвүүлээ серверийн дотоод ажил (materialized
 *      view, хоцорсон төхөөрөмж, ханш, өрсөлдөөний хөлдөөлт). Хэрэглэгч
 *      удирдах зүйлгүй. `GET /system/jobs`, `POST …/trigger` ХЭВЭЭР —
 *      зөвхөн UI-аас нуув.
 *    · «Системийн тогтмол», «Системийн тохиргоо» — кодод бичигдсэн
 *      утга ба хийгдээгүй функцийн тоггл байв. Схемд тохиргооны хүснэгт
 *      байхгүй тул хэрэглэгчийн өөрчлөх тохиргоо НЭГ Ч алга.
 */

import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { formatDateTime, formatQty, formatRate } from '@boots/domain';
import { api, type Envelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button, ErrorNote, Field, Input, Modal, OkNote, Spinner, Tabs } from '../components/ui';
import { DataTable, type Column } from '../components/data-table';
import { PermissionsTab } from './setup/permissions';

type Tab = 'backup' | 'permissions' | 'audit';

const TABS: { key: Tab; label: string }[] = [
  { key: 'backup', label: 'Backup' },
  { key: 'permissions', label: 'Хэрэглэгчийн эрх' },
  { key: 'audit', label: 'Сүүлийн үйлдлүүд' },
];

export function SettingsPage() {
  const { can } = useAuth();
  const isAdmin = can('ADMIN');
  const [tab, setTab] = useState<Tab>('backup');
  const [changingPassword, setChangingPassword] = useState(false);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        {isAdmin ? <Tabs tabs={TABS} value={tab} onChange={setTab} /> : <span />}
        <Button variant="ghost" onClick={() => setChangingPassword(true)}>Нууц үг солих</Button>
      </div>

      {/* Гурвуулаа серверт `adminOnly` — бусад хүнд дэмий 403 харуулахгүй */}
      {isAdmin && tab === 'backup' ? <BackupTab /> : null}
      {isAdmin && tab === 'permissions' ? <PermissionsTab /> : null}
      {isAdmin && tab === 'audit' ? <AuditTab /> : null}

      {changingPassword ? <ChangePassword onClose={() => setChangingPassword(false)} /> : null}
    </div>
  );
}

// ------------------------------------------------------------ Backup
interface BackupStatus {
  state: 'ok' | 'stale' | 'failed' | 'unknown';
  finishedAt: string | null;
  bytes: number | null;
  ageHours: number | null;
  error: string | null;
}

/**
 * Төлвийг серверийн `state`-ээс — `ok` талбараас БИШ: амжилттай ч 48
 * цагаас хэтэрсэн backup нь «stale» (cron зогссон байж болно). Өмнө нь
 * `ok === true` л харж, хоёр хоног зогссон backup-ыг «амжилттай» гэдэг байв.
 */
const BACKUP_STATE: Record<BackupStatus['state'], { text: string; tone: string }> = {
  ok: { text: '✓ Сүүлийн backup амжилттай', tone: 'bg-accent-100 text-accent-800' },
  stale: { text: '⚠️ Backup удаан хийгдээгүй — хуваарь зогссон байж магадгүй', tone: 'bg-alert-bg text-alert-ink' },
  failed: { text: '⚠️ Сүүлийн backup амжилтгүй', tone: 'bg-alert-bg text-alert-ink' },
  unknown: { text: 'Backup-ийн мэдээлэл олдсонгүй', tone: 'bg-well text-ink-2' },
};

function BackupTab() {
  const { data, isPending, error } = useQuery({
    queryKey: ['system-backup'],
    queryFn: () => api<Envelope<BackupStatus>>('/system/backup/status'),
  });

  if (isPending) return <Spinner />;
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>;

  const b = data.data;
  const s = BACKUP_STATE[b.state] ?? BACKUP_STATE.unknown;

  return (
    <section className="flex flex-col gap-3 rounded-card border border-hair bg-panel px-card py-4">
      <div className={`rounded-well px-3.5 py-3 text-[13px] ${s.tone}`}>{s.text}</div>

      <dl className="grid gap-2.5 sm:grid-cols-3">
        <BackupFact
          label="Сүүлд дууссан"
          value={b.finishedAt ? formatDateTime(b.finishedAt) : '—'}
          note={b.ageHours !== null ? `${formatQty(Math.round(b.ageHours))} цагийн өмнө` : undefined}
        />
        <BackupFact
          label="Хэмжээ"
          value={b.bytes !== null ? `${formatRate(b.bytes / 1024 / 1024, 1)} MB` : '—'}
        />
        <BackupFact label="Хуваарь" value="Өдөр бүр" note="сервер дээр автоматаар" />
      </dl>

      {b.error ? <p className="text-[11.5px] text-ink-3">{b.error}</p> : null}
      <p className="text-[12px] leading-relaxed text-ink-2">
        Backup нь бүх өгөгдлийн (борлуулалт, гутал, худалдан авагч, баталгаа)
        хуулбарыг тусдаа хадгалалтад өдөр бүр хадгална. Сервер гэмтвэл
        эндээс сэргээнэ. Улаанаар харагдвал админд яаралтай мэдэгдэнэ үү.
      </p>
    </section>
  );
}

function BackupFact({ label, value, note }: { label: string; value: string; note?: string | undefined }) {
  return (
    <div className="flex flex-col gap-1 rounded-well border border-hair bg-well px-3.5 py-2.5">
      <dt className="text-xs text-ink-2">{label}</dt>
      <dd className="num text-[13.5px]">{value}</dd>
      {note ? <span className="text-[11.5px] text-ink-3">{note}</span> : null}
    </div>
  );
}

// ------------------------------------------------------------ Аудит
interface AuditRow {
  id: string; service: string; action: string;
  entityType: string; entityId: string; createdAt?: string; at?: string;
  user?: { username: string } | null;
}

function AuditTab() {
  const { data, isPending, error } = useQuery({
    queryKey: ['system-audit'],
    queryFn: () => api<{ data: AuditRow[] }>('/system/audit?limit=50'),
  });

  if (isPending) return <Spinner />;
  if (error) return <ErrorNote>{(error as Error).message}</ErrorNote>;

  return (
    <section className="flex flex-col gap-2.5 rounded-card border border-hair bg-panel px-card py-4">
      <span className="text-[12.5px] text-ink-2">бичих үйлдэл бүр бүртгэгдэнэ</span>
      <DataTable rows={data.data} columns={auditColumns()} rowKey={(a) => a.id} />
    </section>
  );
}

/** Аудитын мөр — 640px-ээс доош үйлдэл тус бүрийн карт болно. */
function auditColumns(): Column<AuditRow>[] {
  return [
    {
      key: 'action',
      header: 'Үйлдэл',
      mobile: 'title',
      cell: (a) => <span className="num">{a.action}</span>,
    },
    {
      key: 'entity',
      header: 'Обьект',
      cell: (a) => <span className="text-ink-2">{a.entityType}</span>,
    },
    {
      key: 'user',
      header: 'Хэрэглэгч',
      cell: (a) => <span className="text-ink-2">{a.user?.username ?? '—'}</span>,
    },
    {
      key: 'at',
      header: 'Хугацаа',
      align: 'right',
      mobile: 'value',
      cell: (a) => (
        <span className="text-ink-2">
          {a.createdAt ?? a.at ? formatDateTime((a.createdAt ?? a.at)!) : '—'}
        </span>
      ),
    },
  ];
}

// ------------------------------------------------------------ Нууц үг
function ChangePassword({ onClose }: { onClose: () => void }) {
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [done, setDone] = useState(false);

  const run = useMutation({
    mutationFn: () => api('/auth/change-password', { method: 'POST', body: { currentPassword, newPassword } }),
    onSuccess: () => { setDone(true); setCurrent(''); setNew(''); },
  });

  return (
    <Modal
      title="Нууц үг солих"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Хаах</Button>
          {!done ? (
            <Button
              onClick={() => run.mutate()}
              disabled={!currentPassword || newPassword.length < 8 || run.isPending}
            >
              {run.isPending ? 'Солиж байна…' : 'Солих'}
            </Button>
          ) : null}
        </>
      }
    >
      {done ? <OkNote>Нууц үг солигдлоо.</OkNote> : (
        <>
          <Field label="Одоогийн нууц үг">
            <Input type="password" value={currentPassword} onChange={(e) => setCurrent(e.target.value)} autoFocus />
          </Field>
          <Field label="Шинэ нууц үг (8-аас дээш тэмдэгт)">
            <Input type="password" value={newPassword} onChange={(e) => setNew(e.target.value)} />
          </Field>
          {run.error ? <ErrorNote>{(run.error as Error).message}</ErrorNote> : null}
        </>
      )}
    </Modal>
  );
}
